import { useTranslation } from "react-i18next";
import "./styles.scss";
import { CHART_LEGENDS } from "../constants";
import dayjs from "dayjs";
import SelectComponent from "components/select/Select";
import ButtonComponent from "components/button/Button";
import { apiExportRecordsCsvAuto, apiExportRecordsCsvManual , apiExportCSVAllDataForPatient} from "helpers/api/records";
import * as htmlToImage from "html-to-image";
import jsPDF from "jspdf";

const TableRow = ({
  width,
  data = [],
  title,
  bgColor,
  rowkey,
  t,
  chartDataWithDay,
  daysInMonth,
  treatmentDate,
  dateSelected,
}) => {
  const getDiffDate = (day) => {
    const treatmentDateConvert = dayjs(treatmentDate).format('YYYY/MM/DD')
    var diff = day.diff(treatmentDateConvert, 'd', true);
    if (diff < 0) {
      return 0
    }
    return diff + 1;
  }

  return (
    <div style={{ display: "flex" }}>
      <div className="table-row-title" style={{ background: bgColor }}>
        {title}
      </div>
      {[...new Array(daysInMonth)]?.map((it, idx) => {
        let rowData = data[idx] === undefined ? "" : data[idx];
        if (rowkey === "dayNumber" && typeof data[idx] === "number") {
          var day = `${dayjs(dateSelected).year()}/${dayjs(dateSelected).month() + 1}/${idx + 1}`
          rowData = getDiffDate(dayjs(day));
        }

        if (
          typeof data[idx] === "number" &&
          !["dayNumber", "day"].includes(rowkey)
        ) {
          rowData = data[idx] >= 0 ? data[idx].toFixed(1) : "";
        }

        if (rowkey === "day") {
          rowData = `${dayjs(dateSelected).month() + 1}/${idx + 1}`;
        }

        if (rowkey === "isBedWetting") {
          rowData =
            rowData === true
              ? t(`graph.yes`)
              : rowData === false
                ? t(`graph.no`)
                : "";
        }

        if (
          rowData &&
          ["sleepTime", "bedwettingTime", "toiletTime", "wakeUpTime"].includes(
            rowkey
          )
        ) {
          rowData = dayjs(rowData).format("HH:mm");
        }

        rowData = rowData || "-";

        if (
          dayjs(dateSelected).date() < idx + 1 &&
          !["dayNumber", "day"].includes(rowkey)
        ) {
          rowData = "";
        }

        return (
          <div
            style={{
              borderRight: "none",
              width,
              background:
                rowkey === "isBedWetting" && data[idx] === false ? "#ff0" : "",
            }}
            className="table-row-cell"
          >
            {rowData ? `${rowData}` : ""}
          </div>
        );
      })}
      <div
        style={{
          borderLeft: "2px solid black",
          borderRight: "2px solid black",
          width: `calc(100% - ${(width + 1) * daysInMonth}px - 133px)`,
        }}
        className="table-row-cell"
      ></div>
    </div>
  );
};

const Table = ({
  tableData = {},
  width,
  chartDataWithDay,
  daysInMonth,
  dateSelected,
  exportOption,
  exportType,
  setExportType,
  element,
  setIsLoading,
  treatmentDate,
  patientId
}) => {
  const { t } = useTranslation();

  const EXPORT_OPTIONS_VALUE = {
    PDF: 0,
    CSV_MANUAL: 1,
    CSV_AUTO: 2,
    CSV_ALL_DATA_MANUAL: 3,
    CSV_ALL_DATA_AUTO: 4,
  };

  const handleExport = async () => {
    setIsLoading(true);
    switch (exportType) {
      case EXPORT_OPTIONS_VALUE.PDF:
        exportPDF();
        break;
      case EXPORT_OPTIONS_VALUE.CSV_MANUAL:
        exportCSV(exportType);
        break;
      case EXPORT_OPTIONS_VALUE.CSV_AUTO:
        exportCSV(exportType);
        break;
      default:
        setIsLoading(false);
        break;
    }
  };

  const exportPDF = async () => {
    if (!element.current) {
      setIsLoading(false);
      return;
    }

    const elementChart = element.current;

    // Xử lý SVG image nếu có
    const images1 = elementChart.querySelectorAll('svg image');
    images1.forEach((img) => {
      img.outerHTML = img.outerHTML.replace("xlink:", "");
    });

    // Clone nội dung cần export
    const elementToExport = elementChart.cloneNode(true);

    const images = elementToExport.querySelectorAll('svg image');
    images.forEach((img) => {
      img.outerHTML = img.outerHTML.replace("xlink:", "");
    });

    // Điều chỉnh layout
    elementToExport.style.backgroundImage = 'url(/background_urine.png)';
    elementToExport.style.padding = "10px";
    elementToExport.style.height = "auto";
    elementToExport.style.overflow = "visible";
    elementToExport.style.width = `${elementChart.scrollWidth}px`;
    elementToExport.style.maxHeight = 'none';

    // Loại bỏ .list-legend nếu có
    const table = elementToExport.querySelector('.table-container');
    const legend = table?.querySelector('.list-legend');
    if (legend) table.removeChild(legend);

    // Tạo logo
    const logo = document.createElement('div');
    logo.style.display = "flex";
    logo.style.alignItems = "center";
    logo.style.height = "60px";
    const img = document.createElement('img');
    img.src = "/icons/logo.webp";
    img.alt = "icon-logo";
    img.width = 200;
    img.height = 50;
    logo.appendChild(img);

    // Tiêu đề
    const title = document.createElement('h1');
    title.textContent = t("graph.exportPDF.title_urine");
    title.style.fontWeight = "600";
    title.style.fontSize = "1.8em";
    title.style.margin = "10px 0";

    // Thời gian
    const timeString = document.createElement('span');
    timeString.textContent = `${t("graph.exportPDF.timeStr")}${dateSelected.format(t("graph.chartTime"))}（${dayjs(dateSelected).startOf('month').format('YYYY年MM月DD日')}～${dayjs(dateSelected).endOf('month').format('YYYY年MM月DD日')}）`;
    timeString.style.fontSize = "1.2em";

    // Footer copyright
    const copyright = document.createElement('div');
    copyright.textContent = t("graph.exportPDF.copyright");
    copyright.style.textAlign = "right";
    copyright.style.fontSize = "0.6em";
    copyright.style.marginTop = "20px";

    // Header
    const PDFHeader = document.createElement('div');
    PDFHeader.style.textAlign = "center";
    PDFHeader.style.padding = '10px';
    PDFHeader.style.marginBottom = '10px';
    PDFHeader.appendChild(title);
    PDFHeader.appendChild(timeString);

    // Chèn header, logo và footer vào nội dung
    elementToExport.insertBefore(PDFHeader, elementToExport.firstChild);
    elementToExport.insertBefore(logo, elementToExport.firstChild);

    // Xử lý Highcharts style nếu cần
    const charts = elementToExport.getElementsByClassName('highcharts-background');
    for (const el of charts) {
      el.style.fill = "none";
    }

    const temp = document.createElement('div');
    temp.style.position = 'absolute';
    temp.style.left = '-9999px';
    temp.style.top = '0';
    temp.style.overflow = 'visible';
    temp.style.zIndex = '-1';
    temp.style.background = 'white';

    // Set full size
    const originalWidth = elementChart.scrollWidth;
    const originalHeight = elementChart.scrollHeight;

    temp.style.width = `${originalWidth}px`;
    temp.style.height = `${originalHeight}px`;

    elementToExport.style.width = `${originalWidth}px`;
    elementToExport.style.height = `${originalHeight}px`;

    temp.appendChild(elementToExport);
    document.body.appendChild(temp);

    try {
      // Convert to image (use pixelRatio for better quality and full capture)
      const imgData = await htmlToImage.toPng(elementToExport, {
        pixelRatio: 2, // Giúp tăng độ phân giải
      });

      const img = new Image();
      img.src = imgData;

      await new Promise((resolve) => {
        img.onload = () => resolve();
      });

      // Create PDF
      const pdf = new jsPDF({
        orientation: 'landspace',
        unit: 'mm',
        format: 'a4',
      });

      // A4 size in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Image size in px → mm
      const pxToMm = (px) => px * 0.264583;
      const imgWidthMm = pxToMm(img.naturalWidth);
      const imgHeightMm = pxToMm(img.naturalHeight);

      // Scale to fit page
      const scale = Math.min(pageWidth / imgWidthMm, pageHeight / imgHeightMm);
      const scaledWidth = imgWidthMm * scale;
      const scaledHeight = imgHeightMm * scale;

      const x = (pageWidth - scaledWidth) / 2;
      const y = (pageHeight - scaledHeight) / 2;

      pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);
      pdf.setFontSize(9);
      pdf.text(t("graph.exportPDF.copyright"), pageWidth / 1.5, pageHeight - 5, {
        align: "left",
        marginTop: '10px'
      });

      const fileName = `${t("graph.exportPDF.fileNameBedWetting")}.pdf`;
      pdf.save(fileName);
    } catch (err) {
      console.error("Error exporting PDF:", err);
    } finally {
      document.body.removeChild(temp);
      setIsLoading(false);
    }
  };

  // async function exportElementAsPDF(elementToExport) {
  //   // Tỉ lệ scale DOM (có thể tính toán động nếu muốn)
  //   const scaleFactor = 0.7; // giảm còn 70% kích thước gốc

  //   // Lưu lại transform ban đầu (nếu có)
  //   const originalTransform = elementToExport.style.transform;
  //   const originalTransformOrigin = elementToExport.style.transformOrigin;

  //   // ✅ Scale DOM bằng CSS
  //   elementToExport.style.transform = `scale(${scaleFactor})`;
  //   elementToExport.style.transformOrigin = 'top left';

  //   // Đợi DOM áp dụng style (có thể cần timeout nhẹ)
  //   await new Promise((r) => setTimeout(r, 100));

  //   try {
  //     // Chuyển thành ảnh PNG
  //     const imgData = await htmlToImage.toPng(elementToExport);

  //     // Tạo PDF
  //     const pdf = new jsPDF({
  //       orientation: 'landscape',
  //       unit: 'mm',
  //       format: 'a4',
  //     });

  //     const imgProps = pdf.getImageProperties(imgData);
  //     const imgWidthPx = imgProps.width;
  //     const imgHeightPx = imgProps.height;

  //     const pageWidth = pdf.internal.pageSize.getWidth();   // 297mm
  //     const pageHeight = pdf.internal.pageSize.getHeight(); // 210mm

  //     const imgWidthMm = imgWidthPx * 0.264583;
  //     const imgHeightMm = imgHeightPx * 0.264583;

  //     // Scale ảnh để vừa trang
  //     const scale = Math.min(pageWidth / imgWidthMm, pageHeight / imgHeightMm);
  //     const scaledWidth = imgWidthMm * scale;
  //     const scaledHeight = imgHeightMm * scale;

  //     const x = (pageWidth - scaledWidth) / 2;
  //     const y = (pageHeight - scaledHeight) / 2;

  //     pdf.addImage(imgData, 'PNG', x, y, scaledWidth, scaledHeight);

  //     pdf.save(`${t("graph.exportPDF.fileNameBedWetting")}.pdf`);
  //   } catch (err) {
  //     console.error("Error creating PDF:", err);
  //   } finally {
  //     // ✅ Khôi phục style DOM về ban đầu
  //     elementToExport.style.transform = originalTransform;
  //     elementToExport.style.transformOrigin = originalTransformOrigin;
  //   }
  // }


  const exportCSV = async (type) => {
    const from = dayjs(dateSelected)
      .startOf("month")
      .startOf("day")
      .utc()
      .format("YYYY-MM-DD hh:mm");
    const to = dayjs(dateSelected)
      .endOf("month")
      .endOf("day")
      .utc()
      .format("YYYY-MM-DD hh:mm");

    let res = {};

    switch (type) {
      case EXPORT_OPTIONS_VALUE.CSV_MANUAL:
        res = await apiExportRecordsCsvManual({ from, to, patient_id: patientId });
        break;
      case EXPORT_OPTIONS_VALUE.CSV_AUTO:
        res = await apiExportRecordsCsvAuto({ from, to, patient_id: patientId });
        break;

      case EXPORT_OPTIONS_VALUE.CSV_ALL_DATA_MANUAL:
        res = await apiExportCSVAllDataForPatient(patientId, 'manual');
        break;
       case EXPORT_OPTIONS_VALUE.CSV_ALL_DATA_AUTO:
        res = await apiExportCSVAllDataForPatient(patientId, 'manual');
        break;
      default:
        break;
    }

    if (res.data) {
      window.location.href = res.data;
    }
    setIsLoading(false);
    return false;
  }

  return (
    <div
      style={{
        borderSpacing: 0,
        // marginLeft: "27px",
        background: "white",
        tableLayout: "fixed",
      }}
      className="table-container"
    >
      {tableData &&
        Object.keys(tableData).map((rowkey, idx) => {
          const item = tableData[rowkey];

          return (
            <TableRow
              t={t}
              rowkey={rowkey}
              title={item.title}
              key={idx}
              width={width}
              data={item.data}
              bgColor={item.bgColor}
              treatmentDate={treatmentDate}
              chartDataWithDay={chartDataWithDay}
              daysInMonth={daysInMonth}
              dateSelected={dateSelected}
            />
          );
        })}
      <div className="list-legend">
        {CHART_LEGENDS.map((item) => {
          return (
            <div className="chart-legend">
              <img
                src={item.icon}
                alt=""

              // height="20px"
              />
              {t(item.title)}
            </div>
          );
        })}
        <div className="export-section" style={{ marginTop: "0", paddingBottom: 40 }}>
          <div style={{ marginTop: 10 }}>
            <SelectComponent
              options={exportOption}
              popupClassName="export-section-options"
              defaultValue={exportType}
              onChange={(e) => setExportType(e)}
              optionRender={(option) => {
                return (
                  <div
                    style={{
                      alignItems: "center",
                      fontWeight: "bold",
                    }}
                  >
                    <img
                      src={option?.data?.prefix}
                    />
                    {option.label}
                  </div>
                );
              }}
            />
          </div>
          <ButtonComponent
            background="#035697"
            className="export-button"
            text={t("graph.calendarPage.export")}
            type="button"
            onClick={handleExport}
          />
        </div>
      </div>
    </div>
  );
};

export default Table;
